import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, interval, map, of, startWith, tap } from 'rxjs';
import { CustomerInAppNotificationResponseDto, InAppNotificationService as InAppNotificationApiService } from 'src/api';
import { isInAppNotificationActive, sortInAppNotificationsByPriorityDesc } from 'src/shared/util/in-app-notification.util';
import { BaseService } from './base.service';

const IN_APP_NOTIFICATIONS_STORAGE_KEY = 'inAppNotifications';
const POLLING_INTERVAL_MS = 5 * 60 * 1000;

@Injectable({
  providedIn: 'root',
})
export class InAppNotificationService extends BaseService {
  private allNotificationsSubject = new BehaviorSubject<CustomerInAppNotificationResponseDto[]>(
    this.getFromLocalStorage()
  );
  allNotifications$ = this.allNotificationsSubject.asObservable();

  activeNotifications$ = this.allNotifications$.pipe(
    map((notifications) => sortInAppNotificationsByPriorityDesc(notifications.filter((n) => isInAppNotificationActive(n))))
  );

  topVisibleNotification$ = this.activeNotifications$.pipe(map((notifications) => notifications[0] ?? null));

  hasActiveNotifications$ = this.activeNotifications$.pipe(map((notifications) => notifications.length > 0));

  additionalActiveCount$ = this.activeNotifications$.pipe(map((notifications) => Math.max(notifications.length - 1, 0)));

  private polling = false;

  constructor(
    http: HttpClient,
    private inAppNotificationApiService: InAppNotificationApiService
  ) {
    super(http);
  }

  startPolling() {
    if (this.polling) return;
    this.polling = true;

    interval(POLLING_INTERVAL_MS)
      .pipe(startWith(0))
      .subscribe(() => this.refresh());
  }

  refresh() {
    this.inAppNotificationApiService
      .apiInAppNotificationGet()
      .pipe(
        tap((notifications) => this.saveToLocalStorage(notifications)),
        catchError(() => of(this.getFromLocalStorage()))
      )
      .subscribe((notifications) => this.allNotificationsSubject.next(notifications));
  }

  private saveToLocalStorage(notifications: CustomerInAppNotificationResponseDto[]) {
    localStorage.setItem(IN_APP_NOTIFICATIONS_STORAGE_KEY, JSON.stringify(notifications));
  }

  private getFromLocalStorage(): CustomerInAppNotificationResponseDto[] {
    const data = localStorage.getItem(IN_APP_NOTIFICATIONS_STORAGE_KEY);
    return data ? JSON.parse(data) : [];
  }
}
